import { createContext, useContext, useState, useEffect } from "react";
import styles from "./sidePanel.module.css";
import { useLocation, useNavigate } from "react-router-dom";
import { initIterator } from "./sidePanel.util";
import { MenuList } from "Samarth Module/constants/menu";

// Display-only mapping: map internal labels to new visible text
function getDisplayLabel(label) {
    if (!label) return label;
    try {
        if (String(label).toLowerCase() === 'prioritization matrix') return 'Dynamic Prioritization';
    } catch (e) {
        // ignore
    }
    return label;
}

const MENU_ALIAS_PATHS = {
    "/samarth/norm_alert": ["/samarth/demand"],
};

const isMenuActive = (pathname, menuPath) => {
    if (!menuPath) return false;
    if (pathname === menuPath) return true;
    return (MENU_ALIAS_PATHS[menuPath] || []).includes(pathname);
};

const MenuContext = createContext();

const filterMenusByAccess = (menus, pageAccess, userType) => {
    const accessiblePages = pageAccess.map(access => access.page.toLowerCase());

    return menus.map(menu => {
        if (menu.menus && menu.menus.length > 0) {
            const filteredSubMenus = menu.menus.filter(subMenu => {
                if (subMenu.title.toLowerCase() === "user manager" && userType === "admin") {
                    return true;
                }
                if (subMenu.external === true) {
                    return true;
                }

                return accessiblePages.includes(subMenu.title.toLowerCase());
            });

            return filteredSubMenus.length > 0 ? {
                ...menu,
                menus: filteredSubMenus
            } : null;
        } else {
            return menu;
        }
    }).filter(menu => menu !== null);
};

const MenuItem = ({ menu, depth, isExpanded }) => {
    const { navigate, location } = useContext(MenuContext);

    const handleClick = () => {
        if (menu.path) {
            // Check if the path is an external URL
            if (menu.path.startsWith('http://') || menu.path.startsWith('https://')) {
                window.open(menu.path, '_blank', 'noopener,noreferrer');
            } else {
                navigate(menu.path);
            }
        }
    };

    return (
        <li className={styles["menu-list"]}>
            <div className={`${isExpanded ? styles["sidebar-item-expandable"] : styles["sidebar-item"]} ${isMenuActive(location.pathname, menu.path) && styles["item-active"]} ${menu.title === "Samarth AI" ? styles["samarth-ai-item"] : ""}`}
                onClick={menu.path ? handleClick : undefined}>
                <div className={styles["sidebar-title-box"]}>
                    {getDisplayLabel(isExpanded ? menu?.description : menu?.label)}
                    {menu.icon && (
                        <img
                            src={menu.icon}
                            alt={menu.title}
                            className={`
                                ${styles["sidebar-logo"]}
                                ${isMenuActive(location.pathname, menu.path) && styles["item-icon-active"]}
                                ${menu.title === "Samarth AI" ? styles["samarth-ai-icon"] : "NA"}
                            `}
                        />
                    )}

                    {isExpanded && (
                        <div className={styles["title-description"]}>
                            <span className={`${styles["sidebar-title"]} ${styles["sub-menu-title"]}  ${isMenuActive(location.pathname, menu.path) && styles["item-text-active"]}`}>
                                {getDisplayLabel(menu.title)}
                            </span>
                        </div>
                    )}
                </div>
            </div>
            {/* Always render sub-menus but control their visibility */}
            <ul className={`${styles["sub-menu"]} ${!isExpanded ? styles["sub-menu-collapsed"] : ""}`}>
                {menu.menus && menu.menus.length > 0 &&
                    menu.menus.map((subMenu) => (
                        <MenuItem
                            menu={subMenu}
                            key={subMenu.title}
                            depth={depth + 1}
                            isExpanded={isExpanded}
                        />
                    ))}
            </ul>
        </li>
    );
};

const Menu = ({ menus }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [isExpanded, setIsExpanded] = useState(false);

    return (
        <div
            className={`${styles["sidebar-wrapper"]} ${isExpanded ? styles["expanded"] : ""
                }`}
            onMouseEnter={() => setIsExpanded(true)}
            onMouseLeave={() => setIsExpanded(false)}
        >
            <div className={styles["sidebar-container"]}>
                <MenuContext.Provider value={{ navigate, location }}>
                    <ul className={!isExpanded ? styles["menu"] : styles["menu-expandable"]}>
                        {menus &&
                            menus.length > 0 &&
                            menus.map((menu) => (
                                <MenuItem
                                    menu={menu}
                                    key={menu.title || menu.description}
                                    depth={0}
                                    isExpanded={isExpanded}
                                />
                            ))}
                    </ul>
                </MenuContext.Provider>
            </div>
        </div>
    );
};

const SidePanel = ({ }) => {
    const [filteredMenus, setFilteredMenus] = useState([]);

    useEffect(() => {
        try {
            const userInfoStr = localStorage.getItem('UserInfo');

            if (userInfoStr) {
                const userAccessData = JSON.parse(userInfoStr);

                if (userAccessData && userAccessData.page_access) {
                    const userType = userAccessData.user_info?.userType || '';
                    const filtered = filterMenusByAccess(MenuList, userAccessData.page_access, userType);
                    const processedMenus = initIterator(filtered);
                    setFilteredMenus(processedMenus);
                } else {
                    setFilteredMenus(initIterator(MenuList));
                }
            } else {
                setFilteredMenus(initIterator(MenuList));
            }
        } catch (error) {
            console.error('Error parsing UserInfo from localStorage:', error);
            setFilteredMenus(initIterator(MenuList));
        }
    }, []);

    return <Menu menus={filteredMenus} />;
};

export default SidePanel;